import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {Button, Card, Drawer, Input, message, Select, Spin, Tag, Timeline} from 'antd';
import {ArrowLeftOutlined, FilterOutlined, SearchOutlined, UpOutlined, DownOutlined, DownloadOutlined} from '@ant-design/icons';
import {useQuery, useMutation} from '@apollo/client';
import {DeliveryTask, ParcelPickupStatus, ParcelPickupStatusColor} from '../types/DeliveryTaskType';
import {useTranslation} from 'react-i18next';
import {GET_DELIVERY_TASK_DETAIL} from '../graphql/queries/getDeliveryTaskDetail';
import {GET_DELIVERY_TASK_PARCELS} from '../graphql/queries/getDeliveryTaskParcels';
import {DOWNLOAD_DELIVERY_TASK_TRACKING_NUMBERS} from '../graphql/mutations/downloadDeliveryTaskTrackingNumbers';
import {formatISODateTime} from '../../../../utils/dateTimeUtils';
import {formatStopNumber, parseStopNumberInput} from '../utils/stopNumberUtils';
import TablePaginationBar from '../../../../components/TablePaginationBar';
import WmsDownloadModal from '../../../../components/WmsDownloadModal';

interface DeliveryTaskDetailDrawerProps {
  visible: boolean;
  onClose: () => void;
  taskId: string | null;
  mode?: 'wms' | 'dsp';
  warehouseId?: number;
}

const DeliveryTaskDetailDrawer: React.FC<DeliveryTaskDetailDrawerProps> = ({
  visible,
  onClose,
  taskId,
  mode,
  warehouseId
}) => {
  const { t } = useTranslation();

  // 获取事件显示文本
  const getEventDisplayText = (history: any): string => {
    const eventType = history.eventType;
    
    // 对于包裹转单事件，根据 transferType 区分转入/转出
    if (eventType === 'PARCELS_TRANSFERRED') {
      const data = parseEventData(history);
      if (data.transferType === 'IN') {
        return t('delivery.tasks.events.PARCELS_TRANSFERRED_IN');
      } else if (data.transferType === 'OUT') {
        return t('delivery.tasks.events.PARCELS_TRANSFERRED_OUT');
      }
      // 向后兼容：如果没有 transferType，使用默认文本
      return t('delivery.tasks.events.PARCELS_TRANSFERRED');
    }
    
    const eventTypeMap: { [key: string]: string } = {
      'TASK_CREATED': t('delivery.tasks.events.TASK_CREATED'),
      'DRIVER_CHANGED': t('delivery.tasks.events.DRIVER_CHANGED'),
      'STATUS_CHANGED': t('delivery.tasks.events.STATUS_CHANGED'),
      'DRIVER_PICKUP_START': t('delivery.tasks.events.DRIVER_PICKUP_START'),
      'DRIVER_PICKUP_END': t('delivery.tasks.events.DRIVER_PICKUP_END'),
      'TASK_COMPLETED': t('delivery.tasks.events.TASK_COMPLETED')
    };
    return eventTypeMap[eventType] || eventType;
  };

  // 解析事件数据
  const parseEventData = (history: any) => {
    try {
      return history.dataJson ? JSON.parse(history.dataJson) : {};
    } catch (error) {
      return {};
    }
  };

  // 切换包裹单号列表的展开/折叠状态
  const toggleTrackingNumbers = (historyId: string | number) => {
    const idStr = String(historyId); // 确保转换为字符串
    setExpandedTrackingNumbers(prev => {
      const newSet = new Set(prev);
      if (newSet.has(idStr)) {
        newSet.delete(idStr);
      } else {
        newSet.add(idStr);
      }
      return newSet;
    });
  };

  // 获取事件描述 - 参考 CustomParcelsTimeline 的实现方式
  const getEventDescription = (history: any): string | React.ReactNode => {
    const data = parseEventData(history);
    const eventType = history.eventType;
    
    // 使用对象映射替代 switch 语句
    const eventHandlers = {
      'TASK_CREATED': () => {
        const parts = [t('delivery.tasks.message.taskCreatedSuccessfully')];
        if (data.routeName) parts.push(`${t('delivery.tasks.details.route')}: ${data.routeName}`);
        if (data.assignedParcels !== undefined) parts.push(`${t('delivery.tasks.details.assignedParcels')}: ${data.assignedParcels}`);
        return parts.length > 1 ? parts.join(', ') : parts[0];
      },
      
      'DRIVER_CHANGED': () => {
        const parts = [];
        if (data.oldDriverName && data.newDriverName) {
          const oldDriverInfo = data.oldDriverPhone ? `${data.oldDriverName}-${data.oldDriverPhone}` : data.oldDriverName;
          const newDriverInfo = data.newDriverPhone ? `${data.newDriverName}-${data.newDriverPhone}` : data.newDriverName;
          parts.push(`${t('delivery.tasks.message.driverChangedFrom')} ${oldDriverInfo} ${t('delivery.tasks.message.driverChangedTo')} ${newDriverInfo}`);
        }
        if (data.operatedBy) {
          parts.push(`${t('delivery.tasks.message.operatedBy')}: ${data.operatedBy}`);
        }
        return parts.join(' ');
      },
      
      'STATUS_CHANGED': () => {
        if (data.oldStatus && data.newStatus) {
          return `${t('delivery.tasks.message.statusChangedFrom')} ${data.oldStatus} ${t('delivery.tasks.message.statusChangedTo')} ${data.newStatus}`;
        }
        return data.description || `${data.oldStatus} → ${data.newStatus}`;
      },
      
      'PARCELS_TRANSFERRED': () => {
        // 解析包裹单号列表
        const parseTrackingNumbers = (trackingNumbers: string | undefined): string[] => {
          if (!trackingNumbers) return [];
          // 可能是逗号分隔的字符串，需要分割
          return trackingNumbers.split(',').map(tn => tn.trim()).filter(tn => tn.length > 0);
        };
        
        // 根据 transferType 字段判断是转入还是转出
        if (data.transferType === 'IN') {
          const parts = [];
          
          // {转出操作人} - 使用 fromDriverName（转出司机）作为转出操作人
          const fromDriverName = data.fromDriverName || data.meta?.operatorName || data.operatorName;
          if (fromDriverName) {
            parts.push(fromDriverName);
          }
          
          // 自{转出任务单号}中转来
          if (data.fromTaskId) {
            parts.push(t('delivery.tasks.message.transferIn.fromTask'));
            parts.push(`${t('delivery.tasks.message.transferIn.taskPrefix')}${data.fromTaskId}`);
            parts.push(t('delivery.tasks.message.transferIn.transferSuffix'));
          }
          
          // {转入包裹数量}个包裹
          if (data.parcelCount) {
            parts.push(`${data.parcelCount}${t('delivery.tasks.message.transferIn.parcelsSuffix')}`);
          }
          
          // 构建包裹单号显示部分
          const trackingNumbers = parseTrackingNumbers(data.trackingNumbers);
          if (trackingNumbers.length > 0) {
            const historyIdStr = String(history.id); // 确保转换为字符串
            const isExpanded = expandedTrackingNumbers.has(historyIdStr);
            const displayCount = 3;
            const shouldShowExpand = trackingNumbers.length > displayCount;
            const displayedNumbers = isExpanded ? trackingNumbers : trackingNumbers.slice(0, displayCount);
            
            return (
              <span>
                {parts.join(' ')} {t('delivery.tasks.message.transferIn.openParen')}
                {displayedNumbers.join(', ')}
                {shouldShowExpand && !isExpanded && '...'}
                {t('delivery.tasks.message.transferIn.closeParen')}
                {shouldShowExpand && (
                  <Button
                    type="link"
                    size="small"
                    icon={isExpanded ? <UpOutlined /> : <DownOutlined />}
                    onClick={() => toggleTrackingNumbers(String(history.id))}
                    style={{ padding: 0, marginLeft: 4, height: 'auto', fontSize: '12px' }}
                  >
                    {isExpanded ? t('delivery.tasks.message.collapse') : t('delivery.tasks.message.expand')}
                  </Button>
                )}
              </span>
            );
          }
          
          // 使用空格连接，确保英文版本正确显示
          return parts.join(' ');
          
        } else if (data.transferType === 'OUT') {
          const parts = [];
          
          // {转出包裹数量}个包裹
          if (data.parcelCount) {
            parts.push(`${data.parcelCount}${t('delivery.tasks.message.transferOut.parcelsSuffix')}`);
          }
          
          // 构建包裹单号显示部分
          const trackingNumbers = parseTrackingNumbers(data.trackingNumbers);
          
          // 被{转出操作人}转出至 - 使用 fromDriverName（转出司机）或 meta.operatorName（操作人）
          const operatorName = data.fromDriverName || data.meta?.operatorName || data.operatorName;
          
          // {承接任务单号}
          const toTaskId = data.toTaskId;
          
          // 如果有运单号，先显示包裹数量和运单号，然后显示操作人和承接任务单号
          if (trackingNumbers.length > 0) {
            const historyIdStr = String(history.id); // 确保转换为字符串
            const isExpanded = expandedTrackingNumbers.has(historyIdStr);
            const displayCount = 3;
            const shouldShowExpand = trackingNumbers.length > displayCount;
            const displayedNumbers = isExpanded ? trackingNumbers : trackingNumbers.slice(0, displayCount);
            
            return (
              <span>
                {parts.join(' ')} {t('delivery.tasks.message.transferOut.openParen')}
                {displayedNumbers.join(', ')}
                {shouldShowExpand && !isExpanded && '...'}
                {t('delivery.tasks.message.transferOut.closeParen')}
                {operatorName && (
                  <>
                    {' '}{t('delivery.tasks.message.transferOut.transferredBy')}
                    {' '}{operatorName}
                  </>
                )}
                {toTaskId && (
                  <>
                    {' '}{t('delivery.tasks.message.transferOut.transferredTo')}
                    {' '}{t('delivery.tasks.message.transferOut.taskPrefix')}{toTaskId}
                  </>
                )}
                {shouldShowExpand && (
                  <Button
                    type="link"
                    size="small"
                    icon={isExpanded ? <UpOutlined /> : <DownOutlined />}
                    onClick={() => toggleTrackingNumbers(String(history.id))}
                    style={{ padding: 0, marginLeft: 4, height: 'auto', fontSize: '12px' }}
                  >
                    {isExpanded ? t('delivery.tasks.message.collapse') : t('delivery.tasks.message.expand')}
                  </Button>
                )}
              </span>
            );
          }
          
          // 如果没有运单号，显示完整信息
          if (operatorName) {
            parts.push(t('delivery.tasks.message.transferOut.transferredBy'));
            parts.push(operatorName);
            parts.push(t('delivery.tasks.message.transferOut.transferredTo'));
          }
          
          if (toTaskId) {
            parts.push(`${t('delivery.tasks.message.transferOut.taskPrefix')}${toTaskId}`);
          }
          
          // 使用空格连接，确保英文版本正确显示
          return parts.join(' ');
        }
        
        // 默认处理（向后兼容老数据）
        const transferType = data.transferType || data.transferDetails || '';
        const parts = [transferType || t('delivery.tasks.message.parcelsTransferred')];
        if (data.parcelCount > 0) parts.push(`(${data.parcelCount} ${t('delivery.tasks.details.parcelCount')})`);
        return parts.join(' ');
      },
      
      'DRIVER_PICKUP_START': () => {
        const parts = [];
        if (data.driverName && data.driverPhone) {
          parts.push(`${t('delivery.tasks.message.driverWithPhone')} ${data.driverName}-${data.driverPhone} ${t('delivery.tasks.message.driverStartedPickup')}`);
        } else if (data.driverName) {
          parts.push(`${t('delivery.tasks.message.driver')} ${data.driverName} ${t('delivery.tasks.message.driverStartedPickup')}`);
        } else {
          parts.push(t('delivery.tasks.message.driverStartedPickup'));
        }
        if (data.pickupLocation) parts.push(`${t('delivery.tasks.details.pickupLocation')}: ${data.pickupLocation}`);
        return parts.join(' ');
      },
      
      'DRIVER_PICKUP_END': () => {
        const parts = [];
        if (data.driverName && data.driverPhone) {
          parts.push(`${t('delivery.tasks.message.driverWithPhone')} ${data.driverName}-${data.driverPhone} ${t('delivery.tasks.message.driverPickupFinished')} ${data.actualPickedUpParcels || 0} ${t('delivery.tasks.details.parcelCount')}；`);
        } else if (data.driverName) {
          parts.push(`${t('delivery.tasks.message.driver')} ${data.driverName} ${t('delivery.tasks.message.driverPickupFinished')} ${data.actualPickedUpParcels || 0} ${t('delivery.tasks.details.parcelCount')}；`);
        } else {
          parts.push(t('delivery.tasks.message.driverPickupFinished'));
          if (data.actualPickedUpParcels !== undefined) parts.push(`${data.actualPickedUpParcels} ${t('delivery.tasks.details.parcelCount')}`);
        }
        return parts.join(' ');
      },
      
      'TASK_COMPLETED': () => {
        return t('delivery.tasks.message.taskCompleted');
      }
    };
    
    const handler = eventHandlers[eventType as keyof typeof eventHandlers];
    return handler ? handler() : (history.description || '');
  };
  const [trackingNumber, setTrackingNumber] = useState('');
  const [stopNumber, setStopNumber] = useState<string>('');
  const [selectedZipcode, setSelectedZipcode] = useState<string | undefined>();
  const [selectedStatus, setSelectedStatus] = useState<string | undefined>();
  const [showSearch, setShowSearch] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [showDownloadModal, setShowDownloadModal] = useState(false);
  const [downloadFileName, setDownloadFileName] = useState('');
  const [exportCooldown, setExportCooldown] = useState(false);
  // 记录每个转单事件是否展开包裹单号列表
  const [expandedTrackingNumbers, setExpandedTrackingNumbers] = useState<Set<string>>(new Set());

  const { data, loading, error, refetch } = useQuery(GET_DELIVERY_TASK_DETAIL, {
    variables: { id: taskId ? parseInt(taskId) : null },
    skip: !taskId,
    fetchPolicy: 'network-only'  // ✅ 每次都从网络获取最新数据，不使用缓存
  });

  const [downloadTrackingNumbers, { loading: downloadLoading }] = useMutation(DOWNLOAD_DELIVERY_TASK_TRACKING_NUMBERS);

  // ✅ 每次抽屉打开或任务ID变化时，强制重新查询详情页数据
  useEffect(() => {
    if (visible && taskId) {
      refetch();
    }
  }, [visible, taskId, refetch]);

  // 切换任务时重置筛选条件，避免残留导致"筛不到"
  useEffect(() => {
    setSelectedZipcode(undefined);
    setSelectedStatus(undefined);
    setTrackingNumber('');
    setStopNumber('');
  }, [taskId]);

  // 详情页关闭时清空搜索框并隐藏
  useEffect(() => {
    if (!visible) {
      setSelectedZipcode(undefined);
      setSelectedStatus(undefined);
      setTrackingNumber('');
      setStopNumber('');
      setShowSearch(false);
      setShowFilters(false);
      setPage(1);
      setPageSize(20); // 重置每页条数为默认值
    }
  }, [visible]);

  const deliveryTask: DeliveryTask | undefined = data?.getDeliveryTaskDetail;

  // 后端分页查询包裹
  const [parcels, setParcels] = useState<any[]>([]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [totalParcels, setTotalParcels] = useState(0);

  // 解析 stopNumber：输入 "1" 或 "1-" 时只匹配 stop=1，输入 "1-1" 时匹配 stop=1 且 subStop="1"
  const parsedStop = useMemo(() => {
    const parsed = parseStopNumberInput(stopNumber);
    return {
      stopNumber: parsed ? parsed.stop : null,
      subStopNumber: parsed?.subStop || null
    };
  }, [stopNumber]);

  // 构建查询变量（依赖原始 stopNumber，确保每次输入变化都会生成新的变量对象）
  const queryVariables = useMemo(() => {
    const parsed = parseStopNumberInput(stopNumber);
    return {
      taskId: taskId ? parseInt(taskId) : null,
      filter: {
        trackingNumber: (trackingNumber || '').trim() || null,
        stopNumber: parsed ? parsed.stop : null,
        subStopNumber: parsed?.subStop || null,
        zipcode: selectedZipcode || null,
        pickupStatus: selectedStatus || null,
      },
      page: { page, size: pageSize }
    };
  }, [taskId, trackingNumber, stopNumber, selectedZipcode, selectedStatus, page, pageSize]);

  const { data: parcelsData, loading: parcelsLoading, refetch: refetchParcels } = useQuery(GET_DELIVERY_TASK_PARCELS, {
    variables: queryVariables,
    skip: !taskId,
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true
  });

  // 更新包裹数据
  useEffect(() => {
    if (parcelsData?.getDeliveryTaskParcels) {
      const resp = parcelsData.getDeliveryTaskParcels;
      setParcels(resp.content || []);
      setTotalParcels(Number(resp.totalElements || 0));
    }
  }, [parcelsData]);

  // 当任务或筛选变化时，重置分页到第一页
  useEffect(() => {
    if (!taskId) return;
    setPage(1);
    setPageSize(20); // 重置每页条数为默认值
  }, [taskId, trackingNumber, stopNumber, selectedZipcode, selectedStatus]);

  // 分页变化处理
  const handlePageChange = useCallback((newPage: number, newPageSize?: number) => {
    if (newPageSize && newPageSize !== pageSize) {
      // 如果每页数量发生变化，重置到第一页
      setPageSize(newPageSize);
      setPage(1);
    } else {
      setPage(newPage);
    }
  }, [pageSize]);

  // 下载运单号列表（异步，集成下载中心）
  const handleDownloadTrackingNumbers = async () => {
    if (!taskId) return;

    try {
      const { data } = await downloadTrackingNumbers({
        variables: { taskId: parseInt(taskId), warehouseId: warehouseId || undefined }
      });

      if (data?.downloadDeliveryTaskTrackingNumbers) {
        setDownloadFileName(data.downloadDeliveryTaskTrackingNumbers);
        setShowDownloadModal(true);
        setExportCooldown(true);
        setTimeout(() => setExportCooldown(false), 5000);
      }
    } catch (error: any) {
      console.error('Download tracking numbers failed:', error);
      message.error(error.message || t('delivery.tasks.message.downloadFailed'));
    }
  };

  // 过滤包裹列表
  // 使用后端分页数据
  const filteredParcels = parcels;

  // 获取唯一的zipcode列表用于筛选
  const uniqueZipcodes = useMemo(() => {
    if (!deliveryTask?.parcels) return [];
    return Array.from(new Set(deliveryTask.parcels.map(p => p.zipcode)));
  }, [deliveryTask?.parcels]);

  // 状态转换函数 - 使用 pickupStatus
  const getPickupStatusText = (parcel: any) => {
    const pickupStatus = parcel.pickupStatus;
    
    switch (pickupStatus) {
      case 'PICKED_UP':
        return t('delivery.tasks.pickupStatus.PICKED_UP');
      case 'NOT_PICKED_UP':
        return t('delivery.tasks.pickupStatus.NOT_PICKED_UP');
      case 'TRANSFERRED_OUT':
        return t('delivery.tasks.pickupStatus.TRANSFERRED_OUT');
      case 'TRANSFERRED_IN':
        return t('delivery.tasks.pickupStatus.TRANSFERRED_IN');
      default:
        return t('delivery.tasks.pickupStatus.NOT_PICKED_UP');
    }
  };

  const getPickupStatusColor = (parcel: any) => {
    const pickupStatus = parcel.pickupStatus;
    
    switch (pickupStatus) {
      case 'PICKED_UP':
        return ParcelPickupStatusColor[ParcelPickupStatus.PICKED_UP];
      case 'NOT_PICKED_UP':
        return ParcelPickupStatusColor[ParcelPickupStatus.NOT_PICKED_UP];
      case 'TRANSFERRED_OUT':
        return ParcelPickupStatusColor[ParcelPickupStatus.TRANSFERRED_OUT];
      case 'TRANSFERRED_IN':
        return ParcelPickupStatusColor[ParcelPickupStatus.TRANSFERRED_IN];
      default:
        return ParcelPickupStatusColor[ParcelPickupStatus.NOT_PICKED_UP];
    }
  };

  if (error) {
    message.error(t('delivery.tasks.message.getTaskDetailFailed'));
    return null;
  }

  return (
    <>
    <Drawer
      title={null}
      placement="right"
      // 宽度计算：100vw - 一级菜单(80px) - 二级菜单(224px) = 304px
      width="calc(100vw - 304px)"
      onClose={onClose}
      visible={visible}
      destroyOnClose
      closable={false}
    >
      {loading ? (
        <div className="flex justify-center items-center h-64">
          <Spin size="large" />
        </div>
      ) : deliveryTask ? (
        <div className="flex h-full">
          {/* 左侧：标题 + 任务基础信息模块 */}
          <div className="w-2/3 pr-6 border-r border-gray-200">
            <div className="h-full overflow-y-auto space-y-4">
              {/* 标题区域 */}
              <div className="flex items-center space-x-4 pb-4 border-b border-gray-200">
                <Button
                  type="primary"
                  icon={<ArrowLeftOutlined />}
                  onClick={onClose}
                  className="bg-purple-600 border-purple-600 hover:bg-purple-700"
                >
                  {t('delivery.tasks.label.backToList')}
                </Button>
                <div className="flex items-center">
                  <span className="h-5 border-l-4 border-purple-500 mr-2 mt-1"></span>
                  <span className="text-lg font-semibold">{t('delivery.tasks.label.taskInformation')} -{deliveryTask?.id}</span>
                </div>
              </div>
              {/* Task Information 卡片 */}
              <Card size="small" className="hover:shadow-md transition-shadow">
                <div className="text-center py-0.5">
                  <div className="text-lg font-semibold text-gray-800">{t('delivery.tasks.label.taskInformation')}</div>
                </div>
              </Card>

              {/* Basic Information 卡片 */}
              <Card size="small" className="hover:shadow-md transition-shadow">
                <div className="p-3">
                  <h3 className="text-base font-semibold mb-3 text-gray-800">{t('delivery.tasks.label.basicInformation')}</h3>
                  <div className="space-y-4">
                    {/* 第一行 */}
                    <div className="grid grid-cols-4 gap-6">
                      <div className="text-center">
                        <div className="text-gray-500 text-sm mb-1">{t('delivery.tasks.label.creatTime')}</div>
                        <div className="font-semibold text-sm">{deliveryTask.createdAt ? new Date(deliveryTask.createdAt).toLocaleString('zh-CN') : 'N/A'}</div>
                      </div>
                      <div className="text-center">
                        <div className="text-gray-500 text-sm mb-1">{t('delivery.tasks.label.route')}</div>
                        <div className="font-semibold text-sm">{deliveryTask.routeName}</div>
                      </div>
                      <div className="text-center">
                        <div className="text-gray-500 text-sm mb-1">{t('delivery.tasks.label.originalDriver')}</div>
                        <div className="font-semibold text-sm">
                          {deliveryTask.originalDriverName || '-'}
                        </div>
                      </div>
                      <div className="text-center">
                        <div className="text-gray-500 text-sm mb-1">{t('delivery.tasks.label.actualDriver')}</div>
                        <div className="font-semibold text-sm">
                          {deliveryTask.actualDriverName || '-'}
                        </div>
                      </div>
                    </div>

                    {/* 第二行 */}
                    <div className="grid grid-cols-4 gap-6">
                      <div className="text-center">
                        <div className="text-gray-500 text-sm mb-1">{t('delivery.tasks.label.parcOriginalAssigned')}</div>
                        <div className="font-semibold text-sm">{deliveryTask.assignedParcels || 0}</div>
                      </div>
                      <div className="text-center">
                        <div className="text-gray-500 text-sm mb-1">{t('delivery.tasks.label.driverPickedup')}</div>
                        <div className="font-semibold text-sm">{deliveryTask.actualPickedUpParcels || 0}</div>
                      </div>
                      <div className="text-center">
                        <div className="text-gray-500 text-sm mb-1">{t('delivery.tasks.label.transferIn')}</div>
                        <div className="font-semibold text-sm">{deliveryTask.transferInParcels || 0}</div>
                      </div>
                      <div className="text-center">
                        <div className="text-gray-500 text-sm mb-1">{t('delivery.tasks.label.transferOut')}</div>
                        <div className="font-semibold text-sm">{deliveryTask.transferOutParcels || 0}</div>
                      </div>
                    </div>
                  </div>
                </div>
              </Card>

              {/* Task Change History 卡片 */}
              <Card size="small" className="hover:shadow-md transition-shadow">
                <div className="p-3">
                  <h3 className="text-base font-semibold mb-3 text-gray-800">{t('delivery.tasks.label.taskChangeHistory')}</h3>
                  <Timeline>
                    {deliveryTask.taskHistory && deliveryTask.taskHistory.length > 0 ? (
                      deliveryTask.taskHistory.map((history, index) => (
                          <Timeline.Item key={history.id} color="purple">
                            <div className="text-sm">
                              <div className="font-medium">{getEventDisplayText(history)}</div>
                              <div className="text-gray-500">{formatISODateTime(history.createdAt)}</div>
                              <div className="text-gray-600">{getEventDescription(history)}</div>
                            </div>
                          </Timeline.Item>
                        ))
                    ) : (
                      <Timeline.Item color="gray">
                        <div className="text-sm text-gray-500">
                          <div className="font-medium">{t('delivery.tasks.message.noHistoryRecords')}</div>
                          <div className="text-gray-400">{t('delivery.tasks.message.noHistoryRecordsDescription')}</div>
                        </div>
                      </Timeline.Item>
                    )}
                  </Timeline>
                </div>
              </Card>
            </div>
          </div>

          {/* 右侧：任务内运单列表模块 */}
          <div className="w-1/3 pl-6">
            <div className="h-full flex flex-col">
              {/* Parcel List 标题和数量 */}
              <div className="mb-4">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-lg font-semibold text-gray-800">{t('delivery.tasks.label.parcelList')}</div>
                    <div className="flex items-center gap-2 text-sm text-gray-500 mt-1">
                      <span>{deliveryTask?.actualTotalParcels ?? 0} {t('delivery.tasks.label.parcelCount')}</span>
                      {/* 下载按钮 */}
                      <Button
                          type="link"
                          size="small"
                          icon={<DownloadOutlined/>}
                          className="text-purple-600 hover:text-purple-700 p-0 h-auto"
                          onClick={handleDownloadTrackingNumbers}
                          loading={downloadLoading}
                          disabled={exportCooldown}
                          title={t('delivery.tasks.label.downloadTrackingNumbers') as string}
                      >
                        {t('delivery.tasks.label.download')}
                      </Button>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    {/* 搜索图标 */}
                    <Button
                        type="text"
                        icon={<SearchOutlined/>}
                        className={showSearch ? "text-purple-600" : "text-gray-500 hover:text-purple-600"}
                        onClick={() => setShowSearch(!showSearch)}
                    />
                    {/* 筛选图标 */}
                    <Button
                        type="text"
                        icon={<FilterOutlined/>}
                        className={showFilters ? "text-purple-600" : "text-gray-500 hover:text-purple-600"}
                        onClick={() => setShowFilters(!showFilters)}
                    />
                  </div>
                </div>
              </div>
              
              {/* 操作栏 (搜索和筛选) */}
              <div className="mb-4 space-y-3">
                {/* 搜索行 */}
                {showSearch && (
                  <div className="flex items-center gap-2">
                    <Input
                      placeholder={t('delivery.tasks.label.trackingNo') as string}
                      value={trackingNumber}
                      onChange={(e) => setTrackingNumber(e.target.value)}
                      className="flex-1"
                      allowClear
                    />
                    <Input
                      placeholder={t('delivery.tasks.label.stop') as string}
                      value={stopNumber}
                      onChange={(e) => {
                        const val = e.target.value;
                        // 允许纯数字、"1-"、"1-1"、"1-a"、"1."、"1.1"、"1.b" 格式（允许输入过程中的中间状态）
                        // 子编号可以是数字或字符串（字母、数字、下划线等）
                        if (val === '' || /^(\d+)([-.]([a-zA-Z0-9_]*))?$/.test(val)) {
                          setStopNumber(val);
                        }
                      }}
                      className="flex-1"
                      allowClear
                    />
                  </div>
                )}
                
                {/* 筛选行 */}
                {showFilters && (
                  <div className="flex items-center">
                    <div className="grid grid-cols-2 gap-2 w-full">
                      <Select
                        placeholder={t('delivery.tasks.label.filterByZipcode') as string}
                        value={selectedZipcode}
                        onChange={setSelectedZipcode}
                        allowClear
                      >
                        {uniqueZipcodes.map(zipcode => (
                          <Select.Option key={zipcode} value={zipcode}>
                            {zipcode}
                          </Select.Option>
                        ))}
                      </Select>
                      <Select
                        placeholder={t('delivery.tasks.label.filterByStatus') as string}
                        value={selectedStatus}
                        onChange={setSelectedStatus}
                        allowClear
                      >
                        <Select.Option value="NOT_PICKED_UP">
                          {t('delivery.tasks.pickupStatus.NOT_PICKED_UP')}
                        </Select.Option>
                        <Select.Option value="PICKED_UP">
                          {t('delivery.tasks.pickupStatus.PICKED_UP')}
                        </Select.Option>
                        <Select.Option value="TRANSFERRED_OUT">
                          {t('delivery.tasks.pickupStatus.TRANSFERRED_OUT')}
                        </Select.Option>
                        <Select.Option value="TRANSFERRED_IN">
                          {t('delivery.tasks.pickupStatus.TRANSFERRED_IN')}
                        </Select.Option>
                      </Select>
                    </div>
                  </div>
                )}
              </div>

              {/* 包裹卡片列表 */}
              <div className="flex-1 overflow-y-auto space-y-3 mb-4">
                {parcelsLoading ? (
                  <div className="flex justify-center items-center py-8">
                    <Spin />
                  </div>
                ) : (
                  <>
                    {filteredParcels.map(parcel => (
                      <Card key={parcel.id} size="small" className="hover:shadow-md transition-shadow">
                        {/* 第一行：运单号 */}
                        <div className="text-sm font-medium mb-2">
                          <span className="text-gray-800">{t('delivery.tasks.label.trackingNo')}:</span> <span className="text-purple-600">{parcel.trackingNo}</span>
                        </div>

                        {/* 第二行：Zipcode和Stop，分别在左侧和右侧 */}
                        <div className="flex justify-between items-center mb-2">
                          <span
                              className="text-sm text-gray-600">{t('delivery.tasks.label.zipcode')}: {parcel.zipcode}</span>
                          <span className="text-sm text-gray-600">
                            {t('delivery.tasks.label.stop')}: {formatStopNumber(parcel.stop, parcel.subStopNumber)}
                          </span>
                        </div>

                        {/* 第三行：包裹状态 */}
                        <div className="text-sm text-gray-600">
                          {t('delivery.tasks.label.parcelStatus')}: <Tag color={getPickupStatusColor(parcel)} className="ml-1">
                            {getPickupStatusText(parcel)}
                          </Tag>
                        </div>
                      </Card>
                    ))}

                    {filteredParcels.length === 0 && (
                      <div className="text-center text-gray-500 py-8">
                        {t('delivery.tasks.message.noMatchingParcels')}
                      </div>
                    )}
                  </>
                )}
              </div>

              {/* 分页 */}
              <TablePaginationBar
                total={totalParcels}
                current={page}
                pageSize={pageSize}
                onChange={handlePageChange}
                onShowSizeChange={handlePageChange}
                className="flex flex-col sm:flex-row justify-between items-center gap-2 pt-4 border-t border-gray-200"
                paginationProps={{
                  showLessItems: true,
                  responsive: true,
                  className: 'flex-wrap',
                }}
              />
            </div>
          </div>
        </div>
      ) : (
        <div className="text-center text-gray-500">No data available</div>
      )}
    </Drawer>

    <WmsDownloadModal
        visible={showDownloadModal}
        onClose={() => setShowDownloadModal(false)}
        fileName={downloadFileName}
        downloadPagePath={mode === 'wms' ? '/wms/download' : '/delivery/download'}
    />
  </>
  );
};

export default DeliveryTaskDetailDrawer;